import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { 
  AlertTriangle, Shield, Upload, CheckCircle, Send, 
  Globe, Briefcase, Phone, Mail, User, Bitcoin, 
  Smartphone, FileText, DollarSign, MapPin, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const scamTypes = [
  'Romance Scam',
  'Crypto Investment Scam',
  'Job Offer Scam',
  'WhatsApp Scam',
  'Fake Company / Online Store',
  'Phishing Website',
  'Investment Fraud',
  'Loan Scam',
  'Impersonation Scam',
  'Tech Support Scam',
  'Advance Fee Fraud',
  'Other'
];

const countries = [
  'United Kingdom',
  'United States',
  'Australia',
  'Canada',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Ireland',
  'Other'
];

const ReportScam = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    scamType: '',
    fullName: '',
    email: '',
    phone: '',
    suspectWebsite: '',
    suspectCompany: '',
    suspectPhone: '',
    suspectEmail: '',
    suspectSocial: '',
    suspectWallet: '',
    amountLost: '',
    country: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024;
    
    const validFiles = selectedFiles.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max size is 10MB.`);
        return false;
      }
      return true;
    });
    
    if (files.length + validFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed.`);
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.scamType || !formData.fullName || !formData.email || !formData.description) {
      toast.error('Please fill in all required fields.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });
      files.forEach((file, index) => {
        submitData.append(`evidence_${index}`, file);
      });
      
      const response = await fetch(`${API_URL}/api/report-scam`, {
        method: 'POST',
        body: submitData
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success('Report submitted successfully!');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setIsSubmitted(true);
      toast.success('Report submitted successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
        <Helmet>
          <title>Report Submitted | ActionFraudOne</title>
        </Helmet>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Report Submitted Successfully</h1>
            <p className="text-gray-400 mb-8">
              Thank you for submitting your fraud report. Our team will review the details 
              and may contact you if we require additional information.
            </p>
            <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
              <CardContent className="p-6 text-left">
                <h3 className="text-white font-semibold mb-4">What happens next?</h3>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                    <span>Your report has been logged in our fraud intelligence database</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                    <span>Our analysts will review the information provided</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                    <span>If urgent action is needed, we'll contact you directly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                    <span>Your report helps protect others from similar scams</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
              >
                Return Home
              </Button>
              <Button 
                onClick={() => window.location.href = '/investigations'}
                variant="outline"
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              >
                View Investigation Services
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
      <Helmet>
        <title>Report a Scam | ActionFraudOne</title>
        <meta name="description" content="Report suspicious activity, fraud, or scam entities to ActionFraudOne for review and investigation." />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Report a Scam</h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Submit details of suspicious activity, fraud, or scam entities for review by ActionFraudOne.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-8">
              <Card className="bg-[#0d1d32] border-[#1e40af]/30">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Type of Scam <span className="text-red-400">*</span>
                  </h2>
                  <select name="scamType" value={formData.scamType} onChange={handleChange} required
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#1e40af]/30 rounded-lg text-white focus:border-[#1e40af] focus:ring-2 focus:ring-[#1e40af]/20 outline-none"
                    data-testid="scam-type-select">
                    <option value="">Select scam type...</option>
                    {scamTypes.map(type => (<option key={type} value={type}>{type}</option>))}
                  </select>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d32] border-[#1e40af]/30">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-400" />Your Details
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Full Name <span className="text-red-400">*</span></label>
                      <Input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="John Smith" className="bg-[#0a1628] border-[#1e40af]/30 text-white" data-testid="reporter-name" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Email Address <span className="text-red-400">*</span></label>
                      <Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" className="bg-[#0a1628] border-[#1e40af]/30 text-white" data-testid="reporter-email" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Phone Number</label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+44 7700 900123" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Country</label>
                      <select name="country" value={formData.country} onChange={handleChange} className="w-full px-4 py-2.5 bg-[#0a1628] border border-[#1e40af]/30 rounded-md text-white focus:border-[#1e40af] outline-none">
                        <option value="">Select country...</option>
                        {countries.map(country => (<option key={country} value={country}>{country}</option>))}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d32] border-[#1e40af]/30">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-orange-400" />Suspect / Scammer Details
                  </h2>
                  <p className="text-gray-500 text-sm mb-4">Provide as much information as you have about the suspected scammer.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2"><Globe className="w-4 h-4" /> Website / Domain</label>
                      <Input type="text" name="suspectWebsite" value={formData.suspectWebsite} onChange={handleChange} placeholder="example-scam.com" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Company Name</label>
                      <Input type="text" name="suspectCompany" value={formData.suspectCompany} onChange={handleChange} placeholder="Fake Trading Ltd" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2"><Phone className="w-4 h-4" /> Phone / WhatsApp</label>
                      <Input type="text" name="suspectPhone" value={formData.suspectPhone} onChange={handleChange} placeholder="+44 7700 900456" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2"><Mail className="w-4 h-4" /> Email Address</label>
                      <Input type="email" name="suspectEmail" value={formData.suspectEmail} onChange={handleChange} placeholder="scammer@example.com" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2"><Smartphone className="w-4 h-4" /> Social Media</label>
                      <Input type="text" name="suspectSocial" value={formData.suspectSocial} onChange={handleChange} placeholder="@username or URL" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2"><Bitcoin className="w-4 h-4" /> Crypto Wallet</label>
                      <Input type="text" name="suspectWallet" value={formData.suspectWallet} onChange={handleChange} placeholder="0x1234...abcd" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d32] border-[#1e40af]/30">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />Financial Impact
                  </h2>
                  <div className="max-w-xs">
                    <label className="block text-gray-400 text-sm mb-2">Amount Lost (if applicable)</label>
                    <Input type="text" name="amountLost" value={formData.amountLost} onChange={handleChange} placeholder="£5,000" className="bg-[#0a1628] border-[#1e40af]/30 text-white" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d32] border-[#1e40af]/30">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-400" />Description of Incident <span className="text-red-400">*</span>
                  </h2>
                  <p className="text-gray-500 text-sm mb-4">Please provide a detailed description of what happened.</p>
                  <textarea name="description" value={formData.description} onChange={handleChange} required rows={6}
                    placeholder="Describe the scam in detail. Include how you were contacted, what was promised, any payments made..."
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#1e40af]/30 rounded-lg text-white focus:border-[#1e40af] focus:ring-2 focus:ring-[#1e40af]/20 outline-none resize-none"
                    data-testid="incident-description" />
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d32] border-[#1e40af]/30">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-cyan-400" />Upload Evidence
                  </h2>
                  <p className="text-gray-500 text-sm mb-4">Upload screenshots, emails, or documents (max 5 files, 10MB each).</p>
                  <div className="border-2 border-dashed border-[#1e40af]/30 rounded-lg p-8 text-center cursor-pointer hover:border-[#1e40af]/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400 mb-2">Click to upload or drag and drop</p>
                    <p className="text-gray-600 text-sm">PNG, JPG, PDF up to 10MB each</p>
                    <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={handleFileChange} className="hidden" />
                  </div>
                  {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-[#0a1628] rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-cyan-400" />
                            <span className="text-gray-300 text-sm">{file.name}</span>
                            <span className="text-gray-500 text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <button type="button" onClick={() => removeFile(index)} className="text-gray-500 hover:text-red-400 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-[#1e40af]/20 via-cyan-500/10 to-[#1e40af]/20 border-[#1e40af]/30">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div>
                      <p className="text-white font-semibold mb-1">Ready to submit your report?</p>
                      <p className="text-gray-400 text-sm">All information is handled confidentially.</p>
                    </div>
                    <Button type="submit" disabled={isSubmitting}
                      className="bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg"
                      data-testid="submit-report-btn">
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2"><Send className="w-5 h-5" />Submit Report</span>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm">
              By submitting this report, you confirm the information provided is accurate to the best of your knowledge.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportScam;
